import React, { useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import Tabs from '../components/ui/Tabs';
import Button from '../components/ui/Button';
import { ArrowRightIcon, CalendarIcon, MapPinIcon, ExternalLinkIcon, SearchIcon, FilterIcon, BriefcaseIcon, GraduationCapIcon, BuildingIcon, UserIcon, UsersIcon, TrophyIcon, HeartIcon } from 'lucide-react';
interface AlumniProfile {
  id: string;
  name: string;
  graduationYear: number;
  profession: string;
  company?: string;
  location: string;
  bio: string;
  achievements: string[];
  image: string;
  featured?: boolean;
}
interface AlumniEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  image: string;
}
const Alumni = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSet, setSelectedSet] = useState<string | null>(null);
  const [filteredAlumni, setFilteredAlumni] = useState<AlumniProfile[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  // Featured Alumni - Real distinguished alumni from executive committee
  const alumniProfiles: AlumniProfile[] = [{
    id: '1',
    name: 'Prof. Akintunde Otubu',
    graduationYear: 1989,
    profession: 'National President, IGS Alumni Association',
    company: 'Oil & Gas Sector',
    location: 'Lagos, Nigeria',
    bio: "A distinguished professional with extensive experience in the oil and gas sector. Prof. Otubu has led numerous multi-million dollar projects across West Africa and is passionate about giving back to his alma mater. As National President of the Alumni Association, he champions initiatives that enhance the school's infrastructure and academic excellence.",
    achievements: ['National President, IGS Alumni Association', 'Led multi-million dollar projects across West Africa', 'M.Sc. Engineering Management - Imperial College London', 'Over 25 years in oil and gas sector leadership'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png',
    featured: true
  }, {
    id: '2',
    name: 'Elder Lanre Oyesile',
    graduationYear: 1981,
    profession: 'First Vice President, IGS Alumni Association',
    company: 'Larry Jay Properties Consultant',
    location: 'Lagos, Nigeria',
    bio: 'A seasoned professional with a distinguished career in public service and entrepreneurship. Elder Oyesile served with Surulere Local Government until his statutory retirement in 2019. He currently serves as MD/CEO of Larry Jay Properties Consultant, a division of Larry Jay Printers. His extensive experience in both public administration and private enterprise brings valuable leadership to the alumni association.',
    achievements: ['First Vice President, IGS Alumni Association', 'MD/CEO, Larry Jay Properties Consultant', 'Retired from Surulere Local Government (2019)', 'Extensive experience in public administration and private enterprise'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg',
    featured: true
  }, {
    id: '3',
    name: 'Olumide Tayo-Lawal, Esq.',
    graduationYear: 1987,
    profession: 'National General Secretary, IGS Alumni Association',
    company: 'Legal Practice',
    location: 'Ogun State, Nigeria',
    bio: 'A distinguished legal practitioner and community development advocate. Called to the Nigerian Bar in 2000, he has been in active legal practice while making significant contributions to politics and community development. As National General Secretary of the Alumni Association, he remains deeply passionate about the affairs of his alma mater.',
    achievements: ['National General Secretary, IGS Alumni Association', 'Called to Nigerian Bar (2000)', 'LL.B - University of Benin', 'Active in politics and community development'],
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg',
    featured: true
  }];
  // Alumni events - Using school images only
  const alumniEvents: AlumniEvent[] = [{
    id: '1',
    title: 'Annual Alumni Reunion',
    date: '2023-12-15',
    time: '12:00 PM - 6:00 PM',
    location: 'Isonyin Grammar School Campus',
    description: 'The annual reunion brings together alumni from all graduating classes for a day of networking, reminiscing, and reconnecting with the school. The event includes campus tours, cultural performances, and an evening dinner.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nn93xnwTcDMxPXw6huLouK/WhatsApp_Image_2025-08-14_at_15.45.40_%281%29.jpg'
  }, {
    id: '2',
    title: 'Alumni Career Fair',
    date: '2024-02-20',
    time: '10:00 AM - 4:00 PM',
    location: 'Isonyin Grammar School Hall',
    description: 'Alumni professionals from various fields return to share career insights with current students. The event includes panel discussions, one-on-one mentoring sessions, and networking opportunities.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3DHAcubmTqmT13YpTTNQmr/WhatsApp_Image_2025-10-24_at_10.01.24.jpg'
  }, {
    id: '3',
    title: 'Alumni Fundraising Gala',
    date: '2024-04-05',
    time: '7:00 PM - 11:00 PM',
    location: 'Radisson Blu Hotel, Lagos',
    description: 'An elegant evening of dining and entertainment to raise funds for the Alumni Scholarship Fund and other school development projects. The event includes an auction, awards ceremony, and special performances.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg'
  }, {
    id: '4',
    title: 'Class of 2000 Silver Jubilee',
    date: '2025-07-25',
    time: '2:00 PM - 10:00 PM',
    location: 'Isonyin Grammar School and Local Resort',
    description: 'A special celebration marking 25 years since the Class of 2000 graduated. The day begins with a thanksgiving service and campus visit, followed by a dinner and dance at a local resort.',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xwrtKazFgq7H6urQHRmWav/WhatsApp_Image_2025-10-24_at_10.01.12_%281%29.jpg'
  }];
  const graduationSets = [{
    decade: '1960s',
    years: [1967, 1968, 1969]
  }, {
    decade: '1970s',
    years: [1970, 1971, 1972, 1973, 1974, 1975, 1976, 1977, 1978, 1979]
  }, {
    decade: '1980s',
    years: [1980, 1981, 1982, 1983, 1984, 1985, 1986, 1987, 1988, 1989]
  }, {
    decade: '1990s',
    years: [1990, 1991, 1992, 1993, 1994, 1995, 1996, 1997, 1998, 1999]
  }, {
    decade: '2000s',
    years: [2000, 2001, 2002, 2003, 2004, 2005, 2006, 2007, 2008, 2009]
  }, {
    decade: '2010s',
    years: [2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019]
  }, {
    decade: '2020s',
    years: [2020, 2021, 2022, 2023]
  }];
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  return <div className="min-h-screen bg-white" data-id="element-2760">
      <PageHeader title="Alumni Network" description="Connect with our global network of distinguished alumni who continue to make significant contributions to society." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Alumni',
      path: '/alumni'
    }]} data-id="element-2761" />

      <div className="container mx-auto px-4 py-12" data-id="element-2762">
        {/* Alumni Association Section */}
        <section className="mb-16" id="association" data-id="element-2763">
          <div className="text-center mb-10" data-id="element-2764">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2765">
              Isonyin Grammar School Alumni Association
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2766"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2767">
              The Isonyin Grammar School Alumni Association (IGSAA) connects
              former students worldwide, fostering lifelong relationships and
              supporting the school's development.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-2768">
            <div data-id="element-2769">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2770">
                Founded in 1975, the IGSAA has grown into a vibrant global
                network of over 15,000 alumni who have gone on to make
                significant contributions in various fields including medicine,
                law, business, education, politics, arts, and sports.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2771">
                The association serves as a platform for networking, mentorship,
                and giving back to the alma mater. Through regular events,
                reunions, and digital platforms, alumni stay connected and
                support each other's professional and personal growth.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-2772">
                IGSAA also plays a crucial role in the development of the school
                through fundraising initiatives, infrastructure projects,
                scholarship programs, and mentorship of current students. The
                association's contributions have significantly enhanced the
                quality of education and facilities at Isonyin Grammar School.
              </p>
              <div className="bg-blue-50 p-6 rounded-lg" data-id="element-2773">
                <h3 className="font-bold text-blue-900 mb-4" data-id="element-2774">Our Mission</h3>
                <ul className="space-y-3" data-id="element-2775">
                  <li className="flex items-start" data-id="element-2776">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2777">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2778">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2779" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-2780">
                      To foster lifelong connections among alumni
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2781">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2782">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2783">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2784" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-2785">
                      To support the development and growth of Isonyin Grammar
                      School
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2786">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2787">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2788">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2789" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-2790">
                      To provide mentorship and guidance to current students
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2791">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2792">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2793">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2794" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-2795">
                      To promote the values and reputation of the school in the
                      wider community
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div data-id="element-2796">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bZLbes2hxk7MhXzaZ1hXLV/WhatsApp_Image_2025-10-24_at_10.01.19.jpg" alt="Alumni gathering" className="w-full h-auto rounded-lg shadow-md mb-6" data-id="element-2797" />
              <div className="grid grid-cols-2 gap-4" data-id="element-2798">
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-2799">
                  <div className="text-3xl font-bold mb-1" data-id="element-2800">15,000+</div>
                  <p className="text-sm" data-id="element-2801">Alumni Worldwide</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-2802">
                  <div className="text-3xl font-bold mb-1" data-id="element-2803">48</div>
                  <p className="text-sm" data-id="element-2804">Years of IGSAA</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-2805">
                  <div className="text-3xl font-bold mb-1" data-id="element-2806">₦50M+</div>
                  <p className="text-sm" data-id="element-2807">Raised for School Projects</p>
                </div>
                <div className="bg-blue-900 p-4 rounded-lg text-white text-center" data-id="element-2808">
                  <div className="text-3xl font-bold mb-1" data-id="element-2809">25</div>
                  <p className="text-sm" data-id="element-2810">Global Chapters</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2811">
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2812">
              <div className="flex items-center mb-4" data-id="element-2813">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2814">
                  <UsersIcon className="h-6 w-6 text-blue-700" data-id="element-2815" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2816">Membership</h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2817">
                All former students of Isonyin Grammar School are automatically
                members of the Alumni Association. Active membership requires
                registration and payment of annual dues.
              </p>
              <Button href="/alumni/join" variant="outline" className="w-full" data-id="element-2818">
                Register as a Member
              </Button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2819">
              <div className="flex items-center mb-4" data-id="element-2820">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2821">
                  <CalendarIcon className="h-6 w-6 text-blue-700" data-id="element-2822" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2823">
                  Events & Activities
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2824">
                The association organizes regular events including annual
                reunions, fundraising galas, career fairs, and mentorship
                programs. These events provide opportunities for networking and
                giving back.
              </p>
              <Button href="/alumni/events" variant="outline" className="w-full" data-id="element-2825">
                View Upcoming Events
              </Button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2826">
              <div className="flex items-center mb-4" data-id="element-2827">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-2828">
                  <HeartIcon className="h-6 w-6 text-blue-700" data-id="element-2829" />
                </div>
                <h3 className="text-xl font-bold text-blue-900" data-id="element-2830">Give Back</h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-2831">
                Alumni can support the school through donations, volunteering,
                mentorship, and participating in development projects. Your
                contribution helps ensure a bright future for current and future
                students.
              </p>
              <Button href="/support" variant="outline" className="w-full" data-id="element-2832">
                Support IGS
              </Button>
            </div>
          </div>

          {/* National Executive Committee Leadership */}
          <div className="mt-12 bg-blue-50 p-8 rounded-lg" data-id="element-2833">
            <div className="text-center mb-6" data-id="element-2834">
              <h3 className="text-2xl font-bold text-blue-900 mb-2" data-id="element-2835">
                Alumni Association Leadership
              </h3>
              <p className="text-gray-600" data-id="element-2836">
                Meet the dedicated individuals leading the Isonyin Grammar
                School Alumni Association
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2837">
              {/* National President */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2838">
                <div className="flex flex-col items-center" data-id="element-2839">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png" alt="National President" className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-yellow-500" data-id="element-2840" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2841">
                    Prof. Akintunde Otubu
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2842">
                    National President
                  </p>
                </div>
              </div>

              {/* First Vice President */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2843">
                <div className="flex flex-col items-center" data-id="element-2844">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg" alt="First Vice President" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2845" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2846">
                    Elder Lanre Oyesile
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2847">
                    First Vice President
                  </p>
                </div>
              </div>

              {/* National General Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2848">
                <div className="flex flex-col items-center" data-id="element-2849">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg" alt="National General Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2850" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2851">
                    Olumide Tayo-Lawal, Esq.
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2852">
                    National General Secretary
                  </p>
                </div>
              </div>

              {/* National Treasurer */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2853">
                <div className="flex flex-col items-center" data-id="element-2854">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w7eCBy2QnBuM2eaUMJCtJK/WhatsApp_Image_2025-11-09_at_12.59.17.jpg" alt="National Treasurer" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2855" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2856">
                    SADIQ Adebayo Rasaki
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2857">
                    National Treasurer
                  </p>
                </div>
              </div>

              {/* National Financial Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2858">
                <div className="flex flex-col items-center" data-id="element-2859">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xoPZ5CeiJfFxam6WebTFFc/WhatsApp_Image_2025-11-09_at_14.37.11.jpg" alt="National Financial Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2860" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2861">
                    Abimbola Taiwo Olarewaju
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2862">
                    National Financial Secretary
                  </p>
                </div>
              </div>

              {/* Publicity Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2863">
                <div className="flex flex-col items-center" data-id="element-2864">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gDiHx8kfPCYtVcBF6agkJD/WhatsApp_Image_2025-11-09_at_14.33.59.jpg" alt="Publicity Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2865" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2866">
                    Yeye Adesola Salami
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2867">
                    Publicity Secretary
                  </p>
                </div>
              </div>

              {/* Assistant General Secretary */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2868">
                <div className="flex flex-col items-center" data-id="element-2869">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nR1xAQu1SrTC6snxzfQ7dJ/image.png" alt="Assistant General Secretary" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2870" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2871">
                    Olamide Adesola Oyenuga
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2872">
                    Assistant General Secretary
                  </p>
                </div>
              </div>

              {/* National Social Director */}
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2873">
                <div className="flex flex-col items-center" data-id="element-2874">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4BW49KtAwcy4acgofnGhtA/1000770229.jpg" alt="National Social Director" className="w-32 h-32 rounded-full object-cover mb-4" data-id="element-2875" />
                  <h4 className="font-bold text-blue-900 text-center" data-id="element-2876">
                    Orepekan Kehinde Adewale
                  </h4>
                  <p className="text-sm text-gray-500 mb-1" data-id="element-2877">
                    National Social Director
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center" data-id="element-2878">
              <Button href="/alumni/excos" variant="outline" data-id="element-2879">
                View Full Executive Committee
              </Button>
            </div>
          </div>
        </section>

        {/* Featured Alumni Section */}
        <section className="mb-16" id="featured" data-id="element-2880">
          <div className="text-center mb-10" data-id="element-2881">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2882">
              Featured Alumni
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2883"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2884">
              Celebrating the achievements and contributions of our
              distinguished alumni who are making a difference in various fields
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2885">
            {alumniProfiles.filter(profile => profile.featured).map(alumni => <div key={alumni.id} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-2886">
                  <div className="h-64 overflow-hidden" data-id="element-2887">
                    <img src={alumni.image} alt={alumni.name} className="w-full h-full object-cover" data-id="element-2888" />
                  </div>
                  <div className="p-6" data-id="element-2889">
                    <h3 className="text-xl font-bold text-blue-900 mb-1" data-id="element-2890">
                      {alumni.name}
                    </h3>
                    <div className="flex items-center text-gray-700 font-medium mb-3" data-id="element-2891">
                      <BriefcaseIcon className="h-4 w-4 mr-1" data-id="element-2892" />
                      <span data-id="element-2893">{alumni.profession}</span>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm mb-4" data-id="element-2894">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-2895" />
                      <span data-id="element-2896">{alumni.location}</span>
                    </div>
                    <p className="text-gray-700 line-clamp-3" data-id="element-2897">{alumni.bio}</p>
                  </div>
                </div>)}
          </div>
        </section>

        {/* Alumni Contributions Section */}
        <section className="mb-16 bg-gray-50 py-12" data-id="element-2898">
          <div className="container mx-auto px-4" data-id="element-2899">
            <div className="text-center mb-10" data-id="element-2900">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2901">
                Alumni Contributions
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2902"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2903">
                Our alumni continue to support the school through generous
                donations and facility upgrades
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-2904">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jA5f2egHsPW6YcMhP4RtXv/WhatsApp_Image_2025-10-24_at_10.01.18_%281%29.jpg" alt="Chemistry Laboratory renovation sign by Old Students Association" className="w-full h-auto" data-id="element-2905" />
              <div className="p-6" data-id="element-2906">
                <h3 className="text-2xl font-bold text-blue-900 mb-3" data-id="element-2907">
                  Chemistry Laboratory Renovation
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-2908">
                  The Old Students Association 75/80 Set generously renovated
                  our Chemistry Laboratory, which was commissioned on January
                  27th, 2024. This state-of-the-art facility provides students
                  with an excellent environment for conducting chemistry
                  experiments and practical work.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-2909">
                  The renovation included new laboratory benches, modern
                  equipment, improved ventilation, and safety features that meet
                  international standards. The Senior Parents Club Association
                  also contributed to the provision of science equipment for
                  adequate student development.
                </p>
                <div className="bg-blue-50 p-6 rounded-lg mt-6" data-id="element-2910">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-2911">
                    Other Notable Alumni Contributions
                  </h4>
                  <ul className="space-y-3" data-id="element-2912">
                    <li className="flex items-start" data-id="element-2913">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2914">
                        <span className="text-blue-700 text-sm" data-id="element-2915">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2916">
                        ICT Laboratory donated by Old Students Association 75/80
                        Set
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2917">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2918">
                        <span className="text-blue-700 text-sm" data-id="element-2919">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2920">
                        Science equipment provided by Senior Parents Club
                        Association
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2921">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2922">
                        <span className="text-blue-700 text-sm" data-id="element-2923">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2924">
                        Over ₦50 million raised for school development projects
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2925">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-2926">
                        <span className="text-blue-700 text-sm" data-id="element-2927">✓</span>
                      </div>
                      <span className="text-gray-700" data-id="element-2928">
                        Annual scholarship programs supporting deserving
                        students
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Alumni Directory Section */}
        <section className="mb-16" id="directory" data-id="element-2929">
          <div className="text-center mb-10" data-id="element-2930">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2931">
              Alumni Sets Directory
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2932"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2933">
              Find and connect with your classmates and other alumni by
              graduation year.
            </p>
          </div>
          <div className="flex flex-wrap items-center space-x-2 mb-6" data-id="element-2934">
            <div className="relative" data-id="element-2935">
              <input type="text" placeholder="Search alumni..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2936" />
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2937" />
            </div>
            <button onClick={() => setFilterOpen(!filterOpen)} className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2938">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-2939" />
              Filter by Set
            </button>
          </div>
          {filterOpen && <div className="bg-gray-50 p-4 rounded-lg mb-6 animate-fadeIn" data-id="element-2940">
              <h3 className="font-medium text-gray-900 mb-3" data-id="element-2941">
                Filter by Graduation Year
              </h3>
              <div className="space-y-4" data-id="element-2942">
                {graduationSets.map(set => <div key={set.decade} data-id="element-2943">
                    <h4 className="font-medium text-blue-900 mb-2" data-id="element-2944">
                      {set.decade}
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-2945">
                      {set.years.map(year => <button key={year} onClick={() => setSelectedSet(year.toString())} className={`px-3 py-1 rounded-full text-sm ${selectedSet === year.toString() ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-2946">
                          {year}
                        </button>)}
                    </div>
                  </div>)}
                <div className="pt-2 border-t border-gray-200" data-id="element-2947">
                  <button onClick={() => setSelectedSet(null)} className="px-4 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-medium" data-id="element-2948">
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2949">
            {/* Sample class representatives using real alumni images */}
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2950">
              <div className="flex items-center mb-4" data-id="element-2951">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2952">
                  <div className="text-lg" data-id="element-2953">1989</div>
                  <div className="text-xs" data-id="element-2954">SET</div>
                </div>
                <div data-id="element-2955">
                  <h3 className="font-bold text-blue-900" data-id="element-2956">Class of 1989</h3>
                  <p className="text-sm text-gray-500" data-id="element-2957">125 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2958">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jn44YtV68i3KkaTAFc7QtH/otubu.png" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2959" />
                <div data-id="element-2960">
                  <h4 className="font-medium text-gray-900" data-id="element-2961">
                    Prof. Akintunde Otubu
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2962">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2963">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2964">
              <div className="flex items-center mb-4" data-id="element-2965">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2966">
                  <div className="text-lg" data-id="element-2967">1987</div>
                  <div className="text-xs" data-id="element-2968">SET</div>
                </div>
                <div data-id="element-2969">
                  <h3 className="font-bold text-blue-900" data-id="element-2970">Class of 1987</h3>
                  <p className="text-sm text-gray-500" data-id="element-2971">118 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2972">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5aaAcoc7u7GwjzMdDw1BU9/WhatsApp_Image_2025-11-09_at_14.48.47.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2973" />
                <div data-id="element-2974">
                  <h4 className="font-medium text-gray-900" data-id="element-2975">
                    Olumide Tayo-Lawal, Esq.
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2976">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2977">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2978">
              <div className="flex items-center mb-4" data-id="element-2979">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2980">
                  <div className="text-lg" data-id="element-2981">1986</div>
                  <div className="text-xs" data-id="element-2982">SET</div>
                </div>
                <div data-id="element-2983">
                  <h3 className="font-bold text-blue-900" data-id="element-2984">Class of 1986</h3>
                  <p className="text-sm text-gray-500" data-id="element-2985">132 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2986">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gDiHx8kfPCYtVcBF6agkJD/WhatsApp_Image_2025-11-09_at_14.33.59.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-2987" />
                <div data-id="element-2988">
                  <h4 className="font-medium text-gray-900" data-id="element-2989">
                    Yeye Adesola Salami
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2990">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-2991">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-2992">
              <div className="flex items-center mb-4" data-id="element-2993">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-2994">
                  <div className="text-lg" data-id="element-2995">1983</div>
                  <div className="text-xs" data-id="element-2996">SET</div>
                </div>
                <div data-id="element-2997">
                  <h3 className="font-bold text-blue-900" data-id="element-2998">Class of 1983</h3>
                  <p className="text-sm text-gray-500" data-id="element-2999">105 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-3000">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/w7eCBy2QnBuM2eaUMJCtJK/WhatsApp_Image_2025-11-09_at_12.59.17.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-3001" />
                <div data-id="element-3002">
                  <h4 className="font-medium text-gray-900" data-id="element-3003">
                    SADIQ Adebayo Rasaki
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-3004">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-3005">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-3006">
              <div className="flex items-center mb-4" data-id="element-3007">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-3008">
                  <div className="text-lg" data-id="element-3009">1981</div>
                  <div className="text-xs" data-id="element-3010">SET</div>
                </div>
                <div data-id="element-3011">
                  <h3 className="font-bold text-blue-900" data-id="element-3012">Class of 1981</h3>
                  <p className="text-sm text-gray-500" data-id="element-3013">142 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-3014">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Hew6rxsK47JVpej3qfHsv/WhatsApp_Image_2025-11-10_at_20.52.09.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-3015" />
                <div data-id="element-3016">
                  <h4 className="font-medium text-gray-900" data-id="element-3017">
                    Elder Lanre Oyesile
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-3018">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-3019">
                View Class Directory
              </Button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-3020">
              <div className="flex items-center mb-4" data-id="element-3021">
                <div className="bg-blue-100 text-blue-900 font-bold rounded p-3 text-center min-w-[70px] mr-4" data-id="element-3022">
                  <div className="text-lg" data-id="element-3023">1978</div>
                  <div className="text-xs" data-id="element-3024">SET</div>
                </div>
                <div data-id="element-3025">
                  <h3 className="font-bold text-blue-900" data-id="element-3026">Class of 1978</h3>
                  <p className="text-sm text-gray-500" data-id="element-3027">156 registered alumni</p>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-3028">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xoPZ5CeiJfFxam6WebTFFc/WhatsApp_Image_2025-11-09_at_14.37.11.jpg" alt="Class Representative" className="w-12 h-12 rounded-full object-cover mr-3" data-id="element-3029" />
                <div data-id="element-3030">
                  <h4 className="font-medium text-gray-900" data-id="element-3031">
                    Abimbola Taiwo Olarewaju
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-3032">Class Representative</p>
                </div>
              </div>
              <Button href="/alumni/sets" variant="outline" size="sm" fullWidth data-id="element-3033">
                View Class Directory
              </Button>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-3034">
            <Button href="/alumni/sets" variant="secondary" icon={<ArrowRightIcon data-id="element-3036" />} iconPosition="right" data-id="element-3035">
              View All Graduation Sets
            </Button>
          </div>
        </section>

        {/* Upcoming Alumni Events */}
        <section className="mb-16" data-id="element-3037">
          <div className="text-center mb-10" data-id="element-3038">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-3039">
              Upcoming Alumni Events
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-3040"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-3041">
              Stay connected with your alma mater and fellow alumni through our
              exciting events and gatherings.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3042">
            {alumniEvents.slice(0, 2).map(event => <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col md:flex-row" data-id="element-3043">
                <div className="md:w-2/5 h-48 md:h-auto" data-id="element-3044">
                  <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-3045" />
                </div>
                <div className="md:w-3/5 p-6" data-id="element-3046">
                  <h3 className="text-xl font-bold text-blue-900 mb-3" data-id="element-3047">
                    {event.title}
                  </h3>
                  <div className="flex items-center text-gray-600 mb-2 text-sm" data-id="element-3048">
                    <CalendarIcon className="h-4 w-4 mr-2" data-id="element-3049" />
                    <span data-id="element-3050">{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center text-gray-600 mb-2 text-sm" data-id="element-3051">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3052">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-3053" />
                    </svg>
                    <span data-id="element-3054">{event.time}</span>
                  </div>
                  <div className="flex items-center text-gray-600 mb-3 text-sm" data-id="element-3055">
                    <MapPinIcon className="h-4 w-4 mr-2" data-id="element-3056" />
                    <span data-id="element-3057">{event.location}</span>
                  </div>
                  <p className="text-gray-700 text-sm mb-4 line-clamp-2" data-id="element-3058">
                    {event.description}
                  </p>
                  <Button href="/alumni/events" variant="outline" size="sm" data-id="element-3059">
                    Event Details
                  </Button>
                </div>
              </div>)}
          </div>
          <div className="mt-8 text-center" data-id="element-3060">
            <Button href="/alumni/events" variant="primary" icon={<ArrowRightIcon data-id="element-3062" />} iconPosition="right" data-id="element-3061">
              View All Alumni Events
            </Button>
          </div>
        </section>

        {/* Alumni Achievements */}
        <section className="mb-16" data-id="element-3063">
          <div className="text-center mb-10" data-id="element-3064">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-3065">
              Alumni Achievements
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-3066"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-3067">
              Celebrating the remarkable accomplishments of our alumni across
              various fields.
            </p>
          </div>
          <div className="bg-blue-900 text-white p-8 rounded-lg" data-id="element-3068">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center" data-id="element-3069">
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-3070">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-3071">
                  <GraduationCapIcon className="h-8 w-8 text-yellow-400" data-id="element-3072" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-3073">
                  250+
                </div>
                <p className="text-lg" data-id="element-3074">Professors & Researchers</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-3075">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-3076">
                  <BriefcaseIcon className="h-8 w-8 text-yellow-400" data-id="element-3077" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-3078">
                  500+
                </div>
                <p className="text-lg" data-id="element-3079">Business Leaders & Entrepreneurs</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-3080">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-3081">
                  <TrophyIcon className="h-8 w-8 text-yellow-400" data-id="element-3082" />
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-3083">
                  75+
                </div>
                <p className="text-lg" data-id="element-3084">National Award Recipients</p>
              </div>
              <div className="bg-blue-800/50 p-6 rounded-lg backdrop-blur-sm border border-blue-700/50 transform transition-transform hover:scale-105" data-id="element-3085">
                <div className="inline-block p-4 rounded-full bg-yellow-500/20 mb-4" data-id="element-3086">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-3087">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" data-id="element-3088" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-yellow-400 mb-2" data-id="element-3089">
                  30+
                </div>
                <p className="text-lg" data-id="element-3090">Government Officials</p>
              </div>
            </div>
          </div>
        </section>

        {/* Join Alumni Network CTA */}
        <section data-id="element-3091">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 p-8 rounded-lg text-center" data-id="element-3092">
            <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-3093">
              Join Our Alumni Network
            </h2>
            <p className="text-blue-900 mb-6 max-w-2xl mx-auto" data-id="element-3094">
              Stay connected with your alma mater and fellow alumni. Register to
              receive updates about alumni events, networking opportunities, and
              school developments.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-3095">
              <Button href="/alumni/join" variant="primary" size="lg" data-id="element-3096">
                Register as an Alumnus
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="border-blue-900 text-blue-900 hover:bg-blue-900/10" data-id="element-3097">
                Contact Alumni Office
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>;
};
export default Alumni;